import { SSOCrypto } from '../types';
import { LoggerNames } from '../lib/constants';
import { AuthorizationError } from '../lib/authorization-error';
import { Logger } from '../lib/logger';
import { SSO_TRANSACTION_KEYS } from '../storage-manager/storage.const';
import { SimpleStorage } from '../storage-manager/storage.types';
import { arrayBufferToHexString, generateRandomString, sha256 } from '../lib/crypto';

type StateVerifierConfig = {
  crypto: SSOCrypto;
  transactionStorage: SimpleStorage;
};

export class StateVerifier {
  private readonly crypto: SSOCrypto;
  private readonly logger: Logger = Logger.createLogger(LoggerNames.STATE_VERIFIER);
  private readonly transactionStorage: SimpleStorage;

  constructor({ crypto, transactionStorage }: StateVerifierConfig) {
    this.crypto = crypto;
    this.transactionStorage = transactionStorage;
  }

  /**
   * Create a new id token nonce value and save it in transaction storage
   * Returns a hashed version of the nonce that will be passed to the authorization server.
   */
  async createNonce() {
    const nonce = generateRandomString(32, this.crypto);
    this.transactionStorage.setItem(SSO_TRANSACTION_KEYS.NONCE, nonce);
    return this.hashNonce(nonce);
  }

  /**
   * Verify that the nonce hash returned by the authorization server matches the stored nonce hash.
   * @param nonce
   */
  async verifyNonce(nonce: string | null | undefined) {
    const storedNonce = await this.hashNonce(this.transactionStorage.getItem(SSO_TRANSACTION_KEYS.NONCE));
    if (nonce !== storedNonce) {
      throw new AuthorizationError(
        'NonceMismatch',
        'The nonce returned by the authorization server does not match the stored nonce',
      );
    }
    this.transactionStorage.removeItem(SSO_TRANSACTION_KEYS.NONCE);
  }

  /**
   * Generate a secure random state string and merge it with any user-provided state.
   * This combined state is serialized and stored for later verification.
   * @param userState
   */
  generate(userState: string | undefined): string {
    const state = this.crypto.randomUUID();
    this.logger.debug(`Created new state value: ${state}`);

    const stateObj = {
      state,
      userState,
    };

    const stateString = btoa(JSON.stringify(stateObj));

    this.logger.debug(`Storing encoded state object: ${stateString}`);
    this.transactionStorage.setItem(SSO_TRANSACTION_KEYS.STATE, stateString);

    return stateString;
  }

  /**
   * Decodes and returns the user state from the state parameter which is a base64 encoded string
   * @param state
   */
  decode(state: string) {
    return JSON.parse(atob(state)).userState;
  }

  /**
   * Verifies that the state parameter matches the stored state. Removes
   * the stored state after verification is complete.
   * @param state
   */
  verify(state: string | null): string {
    const storageState = this.transactionStorage.getItem(SSO_TRANSACTION_KEYS.STATE);
    if (typeof state !== 'string' || state !== storageState) {
      this.logger.error('State mismatch. Cannot complete authorization flow');
      throw new AuthorizationError(
        'StateMismatch',
        'The state returned by the authorization server does not match the stored state',
      );
    }
    this.transactionStorage.removeItem(SSO_TRANSACTION_KEYS.STATE);
    return state;
  }

  private async hashNonce(input: string): Promise<string> {
    const hash = await sha256(input, this.crypto);
    return arrayBufferToHexString(hash);
  }
}
